import {zodResolver} from "@hookform/resolvers/zod";
import React, {useContext, useEffect, useRef} from "react";
import {Controller, useForm} from "react-hook-form";
import {InputUtils} from "../../../../helpers";
import {
  Button,
  Checkbox,
  Colors,
  Container,
  FormControl,
  FormControlLabel,
  LoadingButton,
  MenuItem,
} from "../../../../ui";
import {FormHelperText, TextField} from "../../../bounce-ui";
import {getValidatedTextFieldStyles, PaymentsImages} from "../../../components";
import {BankAccountType, bankAccountTypeInfoMap, PaymentDetails, PaymentType} from "../../../declarations";
import {TermsType} from "../terms-and-conditions/components";
import {TermsAndConditionsApproval} from "../terms-and-conditions/TermsAndConditionsApproval";
import {PaymentContext} from "../utils";
import {fillDevAchForm} from "./dev/devAutofill";
import {DevAutofillBar} from "./dev/DevAutofillBar";
import {LargePaymentSplitLabel} from "./LargePaymentsSplitLabel";
import {AchFormValues, PaymentFormCommonProps} from "./types";
import {
  achFormSchema,
  blockAlphabeticInput,
  buildAchDefaults,
  convertACHFormToPaymentDetails,
  parsedFormErrors,
} from "./utils";

export interface AchPaymentFormProps extends PaymentFormCommonProps {
  onAchSubmit: (paymentType: PaymentType, paymentDetails: PaymentDetails, isChangePaymentMethod: boolean) => void;
  submitLabelId?: string;
  termsAndConditionsId?: string;
  isChangePaymentMethod: boolean;
  disable?: boolean;
  primaryEmail?: string;
  userName?: string;
  backToDashboardButton?: boolean;
  handleBackToDashboard?: () => void;
  paymentDetails?: PaymentDetails;
  accountFlexUIState?: any;
  saveFlexPaymentDetails?: (paymentDetails: PaymentDetails) => void;
}

export const AchPaymentForm = ({
  onAchSubmit,
  submitLabelId,
  termsAndConditionsId,
  isChangePaymentMethod,
  primaryEmail,
  userName,
  backToDashboardButton = false,
  handleBackToDashboard,
  paymentDetails,
  saveFlexPaymentDetails,
  accountFlexUIState,
  pageClickEvent,
  pageViewEvent,
  Pages,
  PaymentTermsAndConditionsV2,
  TermsAndConditionsDialog,
  partnerDetails,
  fpPartnerName,
}: AchPaymentFormProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const flexStatePaymentDetails = accountFlexUIState?.payment_plan.payment_details;
  const {disableACHAccountOwnerPrePopulation} = useContext(PaymentContext);
  const bankAccountTypes = InputUtils.getEnumSelectOptions(bankAccountTypeInfoMap);
  const {buttonTouched, setButtonTouched, isLoading, disableButton, showLargeSplitLabel} = useContext(PaymentContext);

  const bankAccountTypesWithDefault = [{value: "", label: "Please select an account type"}, ...bankAccountTypes];

  const formatRoutingNumber = (inputValue: string) => {
    const inputValueWithoutDashes = inputValue.replace(/-/g, "");
    const formattedValue = inputValueWithoutDashes.replace(/(.{3})/g, "$1-");
    return formattedValue.replace(/-$/, "");
  };

  const {handleSubmit, control, watch, formState, getValues, setValue} = useForm<AchFormValues>({
    resolver: zodResolver(achFormSchema),
    defaultValues: buildAchDefaults(
      flexStatePaymentDetails ?? paymentDetails,
      userName,
      primaryEmail,
      disableACHAccountOwnerPrePopulation
    ),
  });

  const formValues = watch();
  const {accountType, email, ...formValuesToValidate} = formValues;

  const hasEmptyFields = Object.values(formValuesToValidate).some((v) => v === "" || v === false);

  const onSubmit = handleSubmit(async (data): Promise<void> => {
    const paymentDetails = {
      ach: {
        account_type: data.accountType as BankAccountType,
        account_name: data.name,
        account_number: InputUtils.removeWhitespaces(data.accountNumber),
        ABA: InputUtils.removeWhitespaces(data.routingNumber),
        state: "",
        zip_code: "",
      },
      email: data.email || undefined,
    };
    setButtonTouched(true);
    onAchSubmit("ACH", paymentDetails, isChangePaymentMethod);
  });

  useEffect(() => {
    const errorsSummary = parsedFormErrors(formState.errors, formValues);
    if (errorsSummary.length > 0) {
      pageClickEvent({
        page: Pages.ACH_FORM,
        cta: "confirm_payment",
        data: {errors: errorsSummary, numberOfErrors: errorsSummary.length},
      });
    }
  }, [formState.errors]);

  const handleSaveForm = () => {
    const formValues = getValues();
    if (formValues) {
      const paymentDetails = convertACHFormToPaymentDetails(formValues);
      saveFlexPaymentDetails?.(paymentDetails);
    }
  };

  useEffect(() => {
    if (saveFlexPaymentDetails) {
      const handleMotion = (e: MouseEvent | KeyboardEvent) => {
        if (
          (e instanceof MouseEvent && ref.current?.contains(e.target as Node)) ||
          (e instanceof KeyboardEvent && e.key === "Tab")
        ) {
          handleSaveForm();
        }
      };
      document.addEventListener("click", handleMotion);
      document.addEventListener("keydown", handleMotion);
      return () => {
        document.removeEventListener("click", handleMotion);
        document.removeEventListener("keydown", handleMotion);
      };
    }
  }, []);

  return (
    <div ref={ref} style={{width: "100%", height: "100%"}}>
      <DevAutofillBar onFill={() => fillDevAchForm({setValue, getValues})} />
      <form autoComplete="off" noValidate onSubmit={onSubmit}>
        <Container>
          {showLargeSplitLabel && <LargePaymentSplitLabel />}
          <Controller
            name="accountNumber"
            control={control}
            render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
              return (
                <TextField
                  {...field}
                  inputRef={ref}
                  fullWidth
                  placeholder={"Your account number (5-17 digits)"}
                  label={"Bank account number"}
                  type="text"
                  error={invalid}
                  helperText={error?.message}
                  onKeyDown={(e) => blockAlphabeticInput(e, 17)}
                  inputProps={{
                    inputMode: "numeric",
                  }}
                  {...getValidatedTextFieldStyles(invalid)}
                />
              );
            }}
          />
          <Controller
            name="routingNumber"
            control={control}
            render={({field: {ref, value, ...field}, fieldState: {invalid, error}}) => {
              return (
                <TextField
                  {...field}
                  inputRef={ref}
                  fullWidth
                  label={"Bank routing number"}
                  placeholder={"XXX-XXX-XXX"}
                  type="text"
                  error={invalid}
                  helperText={error?.message}
                  value={formatRoutingNumber(value || "")}
                  onKeyDown={(e) => blockAlphabeticInput(e, 11)}
                  inputProps={{
                    inputMode: "numeric",
                  }}
                  {...getValidatedTextFieldStyles(invalid)}
                />
              );
            }}
          />
          <Controller
            name="name"
            control={control}
            render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
              return (
                <TextField
                  {...field}
                  inputRef={ref}
                  fullWidth
                  label={"Account owner's name"}
                  type="text"
                  error={invalid}
                  helperText={error?.message}
                  {...getValidatedTextFieldStyles(invalid)}
                />
              );
            }}
          />
          <Controller
            name="accountType"
            control={control}
            render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
              return (
                <TextField
                  {...field}
                  inputRef={ref}
                  fullWidth
                  label={"Bank account type"}
                  error={invalid}
                  helperText={error?.message}
                  select
                  {...getValidatedTextFieldStyles(invalid, true)}
                >
                  {bankAccountTypesWithDefault.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }}
          />
          <Controller
            name="email"
            control={control}
            render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
              return (
                <TextField
                  {...field}
                  inputRef={ref}
                  label={"Email address for receipts"}
                  type="email"
                  error={invalid}
                  helperText={error?.message}
                  fullWidth
                  {...getValidatedTextFieldStyles(invalid)}
                />
              );
            }}
          />
          <PaymentsImages />
          <Controller
            name="confirmTerms"
            control={control}
            render={({field: {ref, value, ...field}, fieldState: {invalid, error}}) => {
              return (
                <FormControl
                  required
                  error={invalid}
                  sx={{
                    marginBottom: invalid ? "22px" : "12px",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        inputRef={ref}
                        size={"small"}
                        checked={value}
                        sx={{alignSelf: "flex-start", marginTop: "0px"}}
                      />
                    }
                    label={
                      <TermsAndConditionsApproval
                        termsAndConditionsId={termsAndConditionsId || TermsType.PAYMENT}
                        page={Pages.PAYMENT_SUMMARY}
                        pageClickEvent={pageClickEvent}
                        pageViewEvent={pageViewEvent}
                        page_T_C={Pages.T_C}
                        TermsAndConditionsDialog={TermsAndConditionsDialog}
                        PaymentTermsAndConditionsV2={PaymentTermsAndConditionsV2}
                        partnerDetails={partnerDetails}
                        fpPartnerName={fpPartnerName}
                        sx={{textAlign: "start"}}
                      />
                    }
                    sx={{alignItems: "flex-start"}}
                  />
                  <FormHelperText sx={{"&.Mui-error": {color: Colors.red._900}}}>{error?.message}</FormHelperText>
                </FormControl>
              );
            }}
          />
          <Container fullWidth sx={{gap: "8px"}}>
            <LoadingButton
              disabled={buttonTouched || disableButton || hasEmptyFields}
              loading={isLoading}
              type={"submit"}
            >
              {submitLabelId || "Confirm payment"}
            </LoadingButton>
            {backToDashboardButton && (
              <Button buttonType={"contained-secondary"} onClick={handleBackToDashboard} fullWidth>
                Back to dashboard
              </Button>
            )}
          </Container>
        </Container>
      </form>
    </div>
  );
};
